import React, { useState, useRef, useEffect } from 'react';
import Typography from '../Typography/Typography';
import { FiUser, FiLogOut } from 'react-icons/fi';

interface UserProfileDropdownProps {
  userName: string;
  company: string;
  onProfileClick: () => void;
  onLogout: () => void;
}

const UserProfileDropdown: React.FC<UserProfileDropdownProps> = ({
  userName,
  company,
  onProfileClick,
  onLogout
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get user initials
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleProfileClick = () => {
    onProfileClick();
    setIsOpen(false);
  };

  const handleLogoutClick = () => {
    onLogout();
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* User Avatar Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 cursor-pointer hover:opacity-90 transition-opacity duration-200"
      >
        {/* Avatar with initials */}
        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm">
          <Typography 
            variant="h6" 
            className="text-[#1A1D44] font-semibold text-lg"
          >
            {getInitials(userName)}
          </Typography>
        </div>
        
     
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50">
          {/* Header Section */}
          <div className="bg-[#1A1D44] px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white rounded-full flex items-center justify-center">
                <Typography 
                  variant="h6" 
                  className="text-[#1A1D44] font-semibold text-lg"
                >
                  {getInitials(userName)}
                </Typography>
              </div>
              <div className="flex flex-col">
                <Typography 
                  variant="h6" 
                  className="text-white font-semibold text-sm"
                >
                  {userName}
                </Typography>
                <Typography 
                  variant="body2" 
                  className="text-white/80 text-xs"
                >
                  {company}
                </Typography>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {/* Profile Option */}
            <button
              onClick={handleProfileClick}
              className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-200 transition-colors duration-200"
            >
              {FiUser({ className: "w-6 h-6 text-gray-600 border border-gray-600 rounded-full p-0.5 " })}
              <Typography 
                variant="body1" 
                className="text-gray-700 font-medium"
              >
                Profile
              </Typography>
            </button>

            {/* Separator */}
            <div className="border-t border-gray-100"></div>

            {/* Logout Option */}
            <button
              onClick={handleLogoutClick}
              className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors duration-200"
            >
              {FiLogOut({ className: "w-5 h-5 text-gray-600" })}
              <Typography 
                variant="body1" 
                className="text-gray-700 font-medium"
              >
                Logout
              </Typography>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileDropdown;
